import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { IconArrowLeft, IconCopy, IconCheck } from '@tabler/icons-react';
import { functions, storage } from '../firebase';
import './EventManagementPage.css';

const emptyForm = { eventId: null, name: '', description: '', tagline: '', brandColor: '', logoUrl: '', heroImageUrl: '', beforeImageUrl: '', afterImageUrl: '', startsAtInput: '', endsAtInput: '', styleTemplateIds: [] };

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const MAX_HERO_BYTES = 5 * 1024 * 1024;
const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function msToLocalInput(ms) {
  if (!ms) return '';
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Minimal admin page to create/edit events (name, exclusive styles, start/end window). Backed
// by the admin-only upsertEvent / listEventsForAdmin callables; getPromptTemplates (admin)
// lists every style so the admin can mark which ones become event-exclusive.
function EventManagementPage({ user }) {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null); // null = checking
  const [events, setEvents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [heroFile, setHeroFile] = useState(null);
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const logoInputRef = useRef(null);
  const heroInputRef = useRef(null);
  const beforeInputRef = useRef(null);
  const afterInputRef = useRef(null);

  const loadEvents = useCallback(async () => {
    const listFn = httpsCallable(functions, 'listEventsForAdmin');
    const res = await listFn();
    setEvents(res.data?.events || []);
  }, []);

  useEffect(() => {
    if (!user) { setAuthorized(false); return; }
    (async () => {
      try {
        await loadEvents();
        const getTemplates = httpsCallable(functions, 'getPromptTemplates');
        const tRes = await getTemplates();
        setTemplates((tRes.data?.templates || []).map((t) => ({ id: t.id, name: t.name })));
        setAuthorized(true);
      } catch (err) {
        console.error('EventManagementPage load failed:', err);
        setAuthorized(err.code === 'functions/permission-denied' ? false : true);
        if (err.code !== 'functions/permission-denied') setError(err.message || 'Failed to load events.');
      }
    })();
  }, [user, loadEvents]);

  const toggleStyle = (id) => {
    setForm((prev) => ({
      ...prev,
      styleTemplateIds: prev.styleTemplateIds.includes(id)
        ? prev.styleTemplateIds.filter((x) => x !== id)
        : [...prev.styleTemplateIds, id],
    }));
  };

  const clearAssetInputs = () => {
    setLogoFile(null);
    setHeroFile(null);
    setBeforeFile(null);
    setAfterFile(null);
    if (logoInputRef.current) logoInputRef.current.value = '';
    if (heroInputRef.current) heroInputRef.current.value = '';
    if (beforeInputRef.current) beforeInputRef.current.value = '';
    if (afterInputRef.current) afterInputRef.current.value = '';
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) { setLogoFile(null); return; }
    if (!file.type.startsWith('image/')) { setError('Logo must be an image file.'); e.target.value = ''; return; }
    if (file.size > MAX_LOGO_BYTES) { setError('Logo must be under 2MB.'); e.target.value = ''; return; }
    setError('');
    setLogoFile(file);
  };

  const handleHeroSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) { setHeroFile(null); return; }
    if (!file.type.startsWith('image/')) { setError('Hero image must be an image file.'); e.target.value = ''; return; }
    if (file.size > MAX_HERO_BYTES) { setError('Hero image must be under 5MB.'); e.target.value = ''; return; }
    setError('');
    setHeroFile(file);
  };

  const handleBeforeSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) { setBeforeFile(null); return; }
    if (!file.type.startsWith('image/')) { setError('Original map must be an image file.'); e.target.value = ''; return; }
    if (file.size > MAX_HERO_BYTES) { setError('Original map must be under 5MB.'); e.target.value = ''; return; }
    setError('');
    setBeforeFile(file);
  };

  const handleAfterSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) { setAfterFile(null); return; }
    if (!file.type.startsWith('image/')) { setError('Generated map must be an image file.'); e.target.value = ''; return; }
    if (file.size > MAX_HERO_BYTES) { setError('Generated map must be under 5MB.'); e.target.value = ''; return; }
    setError('');
    setAfterFile(file);
  };

  const uploadEventAsset = async (eventId, file, baseName) => {
    const ext = file.name.split('.').pop();
    const assetRef = ref(storage, `assets/events/${eventId}/${baseName}.${ext}`);
    await uploadBytes(assetRef, file, { contentType: file.type || undefined });
    return getDownloadURL(assetRef);
  };

  const startEdit = (event) => {
    setError('');
    setForm({
      eventId: event.id,
      name: event.name || '',
      description: event.description || '',
      tagline: event.tagline || '',
      brandColor: event.brandColor || '',
      logoUrl: event.logoUrl || '',
      heroImageUrl: event.heroImageUrl || '',
      beforeImageUrl: event.beforeImageUrl || '',
      afterImageUrl: event.afterImageUrl || '',
      startsAtInput: msToLocalInput(event.startsAtMs),
      endsAtInput: msToLocalInput(event.endsAtMs),
      styleTemplateIds: event.styleTemplateIds || [],
    });
    clearAssetInputs();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => { setForm(emptyForm); setError(''); clearAssetInputs(); };

  const handleSave = async () => {
    setError('');
    if (!form.name.trim()) { setError('Event name is required.'); return; }
    const startsAtMs = form.startsAtInput ? new Date(form.startsAtInput).getTime() : NaN;
    const endsAtMs = form.endsAtInput ? new Date(form.endsAtInput).getTime() : NaN;
    if (!Number.isFinite(startsAtMs) || !Number.isFinite(endsAtMs) || endsAtMs <= startsAtMs) {
      setError('Enter a valid start and end (end must be after start).');
      return;
    }
    const tagline = form.tagline.trim();
    if (tagline.length > 140) { setError('Tagline must be 140 characters or fewer.'); return; }
    const brandColor = form.brandColor.trim();
    if (brandColor && !HEX_COLOR_RE.test(brandColor)) {
      setError('Brand color must be a hex color like #1a2b3c (or empty for the default).');
      return;
    }
    setSaving(true);
    try {
      const upsert = httpsCallable(functions, 'upsertEvent');
      const basePayload = {
        name: form.name.trim(),
        description: form.description.trim(),
        tagline,
        brandColor,
        logoUrl: form.logoUrl,
        heroImageUrl: form.heroImageUrl,
        beforeImageUrl: form.beforeImageUrl,
        afterImageUrl: form.afterImageUrl,
        startsAtMs,
        endsAtMs,
        styleTemplateIds: form.styleTemplateIds,
      };
      const res = await upsert({ eventId: form.eventId || undefined, ...basePayload });
      const savedEventId = res.data?.eventId || form.eventId;
      if (savedEventId && (logoFile || heroFile || beforeFile || afterFile)) {
        const logoUrl = logoFile ? await uploadEventAsset(savedEventId, logoFile, 'logo') : form.logoUrl;
        const heroImageUrl = heroFile ? await uploadEventAsset(savedEventId, heroFile, 'hero') : form.heroImageUrl;
        const beforeImageUrl = beforeFile ? await uploadEventAsset(savedEventId, beforeFile, 'before') : form.beforeImageUrl;
        const afterImageUrl = afterFile ? await uploadEventAsset(savedEventId, afterFile, 'after') : form.afterImageUrl;
        await upsert({ ...basePayload, eventId: savedEventId, logoUrl, heroImageUrl, beforeImageUrl, afterImageUrl });
      }
      resetForm();
      await loadEvents();
    } catch (err) {
      console.error('upsertEvent failed:', err);
      setError(err.message || 'Failed to save the event.');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async (id) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/event/${id}`);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1600);
    } catch (_) { /* clipboard unavailable */ }
  };

  if (authorized === false) {
    return (
      <div className="event-admin-page">
        <div className="event-admin-container">
          <h2>Admins only</h2>
          <p>You need an admin account to manage events.</p>
        </div>
      </div>
    );
  }

  if (authorized === null) {
    return <div className="event-admin-page"><div className="event-admin-container">Loading…</div></div>;
  }

  return (
    <div className="event-admin-page">
      <div className="event-admin-container">
        <button className="event-admin-back" onClick={() => navigate(-1)}>
          <IconArrowLeft size={18} /> Back
        </button>
        <h1>Events</h1>
        <p className="event-admin-sub">Create an event to unlock exclusive styles (time-boxed) and grant claimers 5 💎.</p>

        <div className="event-admin-form">
          <h3>{form.eventId ? 'Edit event' : 'New event'}</h3>

          <label className="event-admin-label">Name</label>
          <input
            className="event-admin-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Tokyo Marathon 2026"
          />

          <label className="event-admin-label">Description</label>
          <textarea
            className="event-admin-input"
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Short description shown on the event page"
          />

          <label className="event-admin-label">Tagline</label>
          <input
            className="event-admin-input"
            maxLength={140}
            value={form.tagline}
            onChange={(e) => setForm({ ...form, tagline: e.target.value })}
            placeholder="Short punchy line shown under the event name (max 140 chars)"
          />

          <label className="event-admin-label">Brand color</label>
          <div className="event-admin-color-row">
            <input
              type="color"
              className="event-admin-color-swatch"
              value={/^#[0-9a-fA-F]{6}$/.test(form.brandColor) ? form.brandColor : '#c98b53'}
              onChange={(e) => setForm({ ...form, brandColor: e.target.value })}
            />
            <input
              className="event-admin-input"
              value={form.brandColor}
              onChange={(e) => setForm({ ...form, brandColor: e.target.value })}
              placeholder="#1a2b3c (empty for the default accent)"
            />
          </div>

          <div className="event-admin-row">
            <div className="event-admin-col">
              <label className="event-admin-label">Partner logo (max 2MB)</label>
              <div className="event-admin-file-row">
                {form.logoUrl && !logoFile && (
                  <img className="event-admin-thumb" src={form.logoUrl} alt="Current logo" />
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  className="event-admin-file"
                  onChange={handleLogoSelect}
                />
              </div>
            </div>
            <div className="event-admin-col">
              <label className="event-admin-label">Hero image (max 5MB)</label>
              <div className="event-admin-file-row">
                {form.heroImageUrl && !heroFile && (
                  <img className="event-admin-thumb" src={form.heroImageUrl} alt="Current hero" />
                )}
                <input
                  ref={heroInputRef}
                  type="file"
                  accept="image/*"
                  className="event-admin-file"
                  onChange={handleHeroSelect}
                />
              </div>
            </div>
          </div>

          <label className="event-admin-label">Landing page before/after pair</label>
          <p className="event-admin-hint">
            The event course as a matching pair, both in portrait: the original plain map and the
            generated art in the event style (grab both from the hero generation). The landing page
            shows them as its before/after and needs both; leave empty for the generic pair.
          </p>
          <div className="event-admin-row">
            <div className="event-admin-col">
              <label className="event-admin-label">Original map, portrait (max 5MB)</label>
              <div className="event-admin-file-row">
                {form.beforeImageUrl && !beforeFile && (
                  <img className="event-admin-thumb" src={form.beforeImageUrl} alt="Current original map" />
                )}
                <input
                  ref={beforeInputRef}
                  type="file"
                  accept="image/*"
                  className="event-admin-file"
                  onChange={handleBeforeSelect}
                />
              </div>
            </div>
            <div className="event-admin-col">
              <label className="event-admin-label">Generated map, portrait (max 5MB)</label>
              <div className="event-admin-file-row">
                {form.afterImageUrl && !afterFile && (
                  <img className="event-admin-thumb" src={form.afterImageUrl} alt="Current generated map" />
                )}
                <input
                  ref={afterInputRef}
                  type="file"
                  accept="image/*"
                  className="event-admin-file"
                  onChange={handleAfterSelect}
                />
              </div>
            </div>
          </div>

          <div className="event-admin-row">
            <div className="event-admin-col">
              <label className="event-admin-label">Starts</label>
              <input
                type="datetime-local"
                className="event-admin-input"
                value={form.startsAtInput}
                onChange={(e) => setForm({ ...form, startsAtInput: e.target.value })}
              />
            </div>
            <div className="event-admin-col">
              <label className="event-admin-label">Ends</label>
              <input
                type="datetime-local"
                className="event-admin-input"
                value={form.endsAtInput}
                onChange={(e) => setForm({ ...form, endsAtInput: e.target.value })}
              />
            </div>
          </div>

          <label className="event-admin-label">Exclusive styles</label>
          <p className="event-admin-hint">Selected styles are hidden from everyone until a user claims this event (and only during the window).</p>
          <div className="event-admin-styles">
            {templates.map((tpl) => (
              <label key={tpl.id} className="event-admin-style">
                <input
                  type="checkbox"
                  checked={form.styleTemplateIds.includes(tpl.id)}
                  onChange={() => toggleStyle(tpl.id)}
                />
                <span>{tpl.name}</span>
              </label>
            ))}
          </div>

          {error && <div className="event-admin-error">{error}</div>}

          <div className="event-admin-actions">
            <button className="event-admin-save" disabled={saving} onClick={handleSave}>
              {saving ? 'Saving…' : form.eventId ? 'Save changes' : 'Create event'}
            </button>
            {form.eventId && (
              <button className="event-admin-cancel" onClick={resetForm}>Cancel edit</button>
            )}
          </div>
        </div>

        <div className="event-admin-list">
          <h3>Existing events</h3>
          {events.length === 0 ? (
            <p className="event-admin-empty">No events yet.</p>
          ) : (
            events.map((ev) => (
              <div key={ev.id} className="event-admin-item">
                <div className="event-admin-item-main">
                  <span className="event-admin-item-name">{ev.name}</span>
                  <span className="event-admin-item-meta">
                    {ev.styleTemplateIds.length} style{ev.styleTemplateIds.length === 1 ? '' : 's'} ·{' '}
                    {new Date(ev.startsAtMs).toLocaleDateString()} – {new Date(ev.endsAtMs).toLocaleDateString()}
                  </span>
                </div>
                <div className="event-admin-item-actions">
                  <button className="event-admin-linkbtn" onClick={() => copyLink(ev.id)}>
                    {copiedId === ev.id ? <IconCheck size={15} /> : <IconCopy size={15} />}
                    {copiedId === ev.id ? 'Copied' : 'Link'}
                  </button>
                  <button className="event-admin-editbtn" onClick={() => startEdit(ev)}>Edit</button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default EventManagementPage;
